import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import Swal from "sweetalert2";

import PageTitle from "../../layouts/PageTitle";
import { appApi, useGetTechnicalEmployeesQuery } from "../../../store/appApi";
import { selectUser, selectUserRole } from "../../../store/authSlice";
import { formatRoleLabel, getFormTypeForRole } from "./reportingConfig";
import { bulkImportFieldReportsApi } from "../../../services/staffReporting";
import {
  downloadFieldImportTemplate,
  parseFieldImportWorkbook,
} from "./bulkImportFieldSheet";

const panelClass =
  "rounded-3xl border border-gray-200 bg-white shadow-xl shadow-slate-200/70";
const labelClass =
  "mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-gray-500";
const inputClass =
  "w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-executive-500 focus:ring-2 focus:ring-executive-500/20";

// Roles that own a Field Execution Journal (formType 1).
const FIELD_ROLES = [
  "Civil Engineer",
  "Project Manager",
  "Supervisor",
  "Foreman",
];

const BulkImportFieldSheets = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentUser = useSelector(selectUser);
  const userRole = useSelector(selectUserRole);
  const isSuperAdmin = userRole === "super-admin";
  const canImport = isSuperAdmin || getFormTypeForRole(userRole) === 1;

  const { data: technicalData } = useGetTechnicalEmployeesQuery(undefined, {
    skip: !isSuperAdmin,
  });
  const fieldStaff = useMemo(
    () =>
      (Array.isArray(technicalData?.staff) ? technicalData.staff : []).filter(
        (person) => FIELD_ROLES.includes(person.role),
      ),
    [technicalData],
  );

  const [employeeId, setEmployeeId] = useState(
    isSuperAdmin ? "" : currentUser?.localId || "",
  );
  const [fileName, setFileName] = useState("");
  const [parsed, setParsed] = useState(null); // { units, warnings, rowCount }
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);

  const totals = useMemo(() => {
    const units = parsed?.units || [];
    const mains = units.reduce((s, u) => s + u.mainWorks.length, 0);
    const subs = units.reduce(
      (s, u) => s + u.mainWorks.reduce((n, m) => n + m.subWorks.length, 0),
      0,
    );
    return { units: units.length, mains, subs };
  }, [parsed]);

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setFileName(file.name);
    setParsed(null);
    setParsing(true);
    try {
      const result = await parseFieldImportWorkbook(file);
      setParsed(result);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Could not read the file",
        text: "Make sure it is an .xlsx file built from the template.",
      });
    } finally {
      setParsing(false);
    }
  };

  const handleImport = async () => {
    if (!employeeId) {
      Swal.fire({
        icon: "warning",
        title: "Select an employee",
        text: "Choose whose reporting sheets these are.",
      });
      return;
    }
    if (!parsed?.units?.length) return;

    try {
      setImporting(true);
      const { data } = await bulkImportFieldReportsApi({
        employeeId,
        units: parsed.units,
      });
      dispatch(appApi.util.invalidateTags(["WorkProgressReports", "Staff"]));
      const errs = Array.isArray(data?.errors) ? data.errors : [];
      Swal.fire({
        icon: errs.length ? "warning" : "success",
        title: "Import finished",
        html: [
          `<b>${data?.created || 0}</b> sheet(s) created`,
          `<b>${data?.merged || 0}</b> merged into existing`,
          data?.skipped ? `<b>${data.skipped}</b> skipped` : "",
          errs.length
            ? `<hr/><div style="text-align:left;font-size:12px">${errs
                .slice(0, 10)
                .join("<br/>")}</div>`
            : "",
        ]
          .filter(Boolean)
          .join("<br/>"),
      }).then(() => navigate("/staff-reporting"));
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Import failed",
        text:
          error?.response?.data?.message ||
          "Something went wrong while importing the sheet.",
      });
    } finally {
      setImporting(false);
    }
  };

  if (!canImport) {
    return (
      <>
        <PageTitle activeMenu="Staff Reporting" motherMenu="Workforce" />
        <div className={`${panelClass} p-8 text-center text-sm text-gray-500`}>
          Bulk import is available to Supervisor, Civil Engineer and Project
          Manager roles only.
        </div>
      </>
    );
  }

  return (
    <>
      <PageTitle activeMenu="Staff Reporting" motherMenu="Workforce" />
      <div className="space-y-6">
        <section className={`${panelClass} p-6`}>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="inline-flex rounded-full border border-executive-200 bg-executive-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-executive-700">
                Bulk Import
              </div>
              <h1 className="mt-4 text-3xl font-semibold text-gray-900">
                Field Execution Journal — Bulk Import
              </h1>
              <p className="mt-2 max-w-3xl text-sm text-gray-500">
                Upload one spreadsheet to create many unit sheets at once. Fill
                the template with one row per sub-work; leave Unit / Main Work
                blank to reuse the row above.
              </p>
            </div>
            <button
              type="button"
              className="rounded-full border-2 border-executive-500 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-gray-700 transition hover:bg-executive-50"
              onClick={() => navigate("/staff-reporting")}
            >
              Back to list
            </button>
          </div>
        </section>

        <section className={`${panelClass} space-y-5 p-6`}>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelClass}>Employee</label>
              {isSuperAdmin ? (
                <select
                  className={inputClass}
                  value={employeeId}
                  onChange={(event) => setEmployeeId(event.target.value)}
                >
                  <option value="">Select an employee…</option>
                  {fieldStaff.map((person) => (
                    <option key={person._id} value={person._id}>
                      {person.name} — {formatRoleLabel(person.role)}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  className={`${inputClass} bg-gray-50`}
                  value={`${currentUser?.name || "You"} — ${formatRoleLabel(
                    userRole,
                  )}`}
                  readOnly
                />
              )}
            </div>

            <div className="flex items-end">
              <button
                type="button"
                className="rounded-xl border-2 border-executive-500 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-gray-700 transition hover:bg-executive-50"
                onClick={downloadFieldImportTemplate}
              >
                Download template (.xlsx)
              </button>
            </div>
          </div>

          <div>
            <label className={labelClass}>Filled spreadsheet</label>
            <div className="flex flex-wrap items-center gap-3">
              <label className="cursor-pointer rounded-xl bg-executive-600 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-white transition hover:bg-executive-500">
                Choose .xlsx file
                <input
                  type="file"
                  accept=".xlsx"
                  className="hidden"
                  onChange={handleFile}
                />
              </label>
              {fileName && (
                <span className="text-sm text-gray-600">{fileName}</span>
              )}
              {parsing && (
                <span className="text-sm text-gray-400">Reading…</span>
              )}
            </div>
          </div>

          {parsed && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-3 text-xs text-gray-600">
                <span className="rounded-full bg-gray-100 px-3 py-1 font-semibold">
                  {totals.units} unit{totals.units === 1 ? "" : "s"}
                </span>
                <span className="rounded-full bg-gray-100 px-3 py-1 font-semibold">
                  {totals.mains} main work{totals.mains === 1 ? "" : "s"}
                </span>
                <span className="rounded-full bg-gray-100 px-3 py-1 font-semibold">
                  {totals.subs} sub-work{totals.subs === 1 ? "" : "s"}
                </span>
              </div>

              {parsed.warnings.length > 0 && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                  <div className="mb-1 font-semibold uppercase tracking-[0.18em]">
                    {parsed.warnings.length} row warning
                    {parsed.warnings.length === 1 ? "" : "s"}
                  </div>
                  <ul className="list-disc space-y-0.5 pl-4">
                    {parsed.warnings.slice(0, 12).map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                    {parsed.warnings.length > 12 && (
                      <li>…and {parsed.warnings.length - 12} more</li>
                    )}
                  </ul>
                </div>
              )}

              {totals.units === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
                  Nothing importable was found in the file.
                </div>
              ) : (
                <div className="max-h-[55vh] space-y-3 overflow-auto rounded-xl border border-gray-200 p-3">
                  {parsed.units.map((unit) => (
                    <div
                      key={unit.unitName}
                      className="rounded-xl border border-gray-200 bg-gray-50 p-3"
                    >
                      <div
                        className="font-semibold"
                        style={{ fontSize: "16px", color: "#222222" }}
                      >
                        {unit.unitName}
                      </div>
                      <div className="mt-2 space-y-2">
                        {unit.mainWorks.map((main, mi) => (
                          <div
                            key={`${main.mainWorkName}-${mi}`}
                            className="rounded-lg border border-gray-200 bg-white p-2.5"
                          >
                            <div className="flex flex-wrap items-center gap-2 text-sm font-medium text-gray-800">
                              {main.mainWorkName || "Untitled work"}
                              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-600">
                                {main.status || "Pending"}
                              </span>
                              <span className="text-[11px] text-gray-400">
                                {main.startDate || "—"} → {main.completionDate || "—"}
                              </span>
                            </div>
                            {main.subWorks.length > 0 && (
                              <ul className="mt-1.5 space-y-1 border-t border-gray-100 pt-1.5 text-xs text-gray-600">
                                {main.subWorks.map((sub, si) => (
                                  <li
                                    key={`${sub.subWorkName}-${si}`}
                                    className="flex flex-wrap items-center gap-2"
                                  >
                                    <span className="font-medium text-gray-700">
                                      {sub.subWorkName}
                                    </span>
                                    <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-600">
                                      {sub.status || "Pending"}
                                    </span>
                                    <span className="text-[10px] text-gray-400">
                                      {sub.startDate || "—"} → {sub.completionDate || "—"}
                                    </span>
                                    {sub.remarks && (
                                      <span className="text-[10px] text-gray-400">
                                        · {sub.remarks}
                                      </span>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <button
                type="button"
                className="rounded-full bg-executive-600 px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-white transition hover:bg-executive-500 disabled:cursor-not-allowed disabled:opacity-60"
                onClick={handleImport}
                disabled={importing || totals.units === 0 || !employeeId}
              >
                {importing
                  ? "Importing…"
                  : `Import ${totals.units} unit sheet${totals.units === 1 ? "" : "s"}`}
              </button>
            </div>
          )}
        </section>
      </div>
    </>
  );
};

export default BulkImportFieldSheets;
